import { Link } from "react-router-dom";
import {
  Zap,
  MapPin,
  Clock,
  ShieldCheck,
  Award,
  LifeBuoy,
  Phone,
  MessageCircle,
  CheckCircle2,
  ArrowRight,
} from "lucide-react";
import SeoHead from "@/components/SeoHead";
import PageHeader from "@/components/layout/PageHeader";
import ServiceChecklistSection from "@/components/sections/ServiceChecklistSection";
import BrandShowcaseSection from "@/components/sections/BrandShowcaseSection";
import { BRAND, buildWhatsAppLink, IMAGES } from "@/data/site";

const HIGHLIGHTS = [
  {
    icon: MapPin,
    title: "Eskişehir Genelinde",
    text: "Tüm ilçe ve mahallelere yerinde akü değişimi.",
  },
  {
    icon: Clock,
    title: "Aynı Gün Servis",
    text: "Arama anından itibaren hızlıca adresinize geliyoruz.",
  },
  {
    icon: MessageCircle,
    title: "WhatsApp ile Konum",
    text: "Konum gönderin, doğru adrese hızlıca ulaşalım.",
  },
  {
    icon: LifeBuoy,
    title: "6 Ay Yol Yardımı",
    text: "Akü hizmeti alan müşterilere 6 ay ücretsiz yol yardımı.",
  },
  {
    icon: ShieldCheck,
    title: "2 Yıl Garanti",
    text: "Tüm standart akülerde 2 yıl üretici garantisi.",
  },
  {
    icon: Award,
    title: "3 Yıl Start-Stop Garanti",
    text: "BSG ve AGM Start-Stop akülerde 3 yıl garanti.",
  },
];

export default function AcilAkuDegisimi() {
  const waText =
    "Merhaba, Eskişehir'de yolda kaldım. Acil akü değişimi için konumumu paylaşıyorum.";

  return (
    <div data-testid="acil-aku-degisimi-page">
      <SeoHead
        title="Eskişehir Acil Akü Değişimi | 7/24 Yerinde Akü Hizmeti"
        description="Eskişehir'de yolda kaldınız mı? Star Otogaz olarak yerinde akü değişimi hizmeti sunuyoruz. İnci Akü, Varta ve Bosch aküler aynı gün montaj yapılır."
        canonical="https://eskisehirakudegisim.com.tr/eskisehir-acil-aku-degisimi"
      />

      <PageHeader
        eyebrow="Acil · Yerinde Hizmet"
        title="Eskişehir Acil"
        accent="Akü Değişimi."
        subtitle="Eskişehir'de yolda mı kaldınız? Star Otogaz olarak adresinize gelip akü değişiminizi profesyonel ekipmanlarla, garantili akülerle yapıyoruz. WhatsApp'tan konumunuzu gönderin, hızlıca yola çıkalım."
      />

      {/* Hero CTA strip */}
      <section className="relative -mt-2 pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl glass-strong p-6 sm:p-8 grid md:grid-cols-[1fr_auto_auto] gap-4 items-center">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#25D366]/15 border border-[#25D366]/40 flex items-center justify-center flex-shrink-0">
                <Zap className="w-5 h-5 text-[#25D366]" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-gold font-bold mb-1">
                  Acil Hat
                </div>
                <div className="font-display text-lg sm:text-xl font-bold text-white">
                  Eskişehir'in her noktasına yerinde akü değişimi
                </div>
              </div>
            </div>
            <a
              href={`tel:${BRAND.phoneTel}`}
              data-testid="acil-call-cta"
              className="gradient-gold text-black font-semibold px-6 py-3.5 inline-flex items-center gap-2 justify-center hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] transition-all"
            >
              <Phone className="w-4 h-4" /> {BRAND.phone}
            </a>
            <a
              href={buildWhatsAppLink(waText)}
              target="_blank"
              rel="noreferrer"
              data-testid="acil-whatsapp-cta"
              className="bg-[#25D366] hover:bg-[#1ebd5a] text-white font-semibold px-6 py-3.5 inline-flex items-center gap-2 justify-center transition-all"
            >
              <MessageCircle className="w-4 h-4" /> Konum Gönder
            </a>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 spotlight-gold opacity-30 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12 max-w-2xl">
            <p className="text-gold uppercase tracking-[0.4em] text-xs mb-3">
              Neden Yerinde Akü Değişimi?
            </p>
            <h2 className="font-display text-3xl sm:text-5xl font-bold leading-[1.05] text-balance">
              <span className="text-gradient-silver">Yolda kalmadan, </span>
              <span className="text-gradient-gold">çekici beklemeden çözüm.</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {HIGHLIGHTS.map(({ icon: Icon, title, text }, i) => (
              <div
                key={title}
                data-testid={`acil-highlight-${i}`}
                className="group glass rounded-2xl p-7 card-lift hover:border-gold/50"
              >
                <div className="w-12 h-12 rounded-xl bg-gold/10 border border-gold/30 flex items-center justify-center mb-5 group-hover:bg-gold/20 group-hover:scale-110 transition-all duration-500">
                  <Icon className="w-5 h-5 text-gold" />
                </div>
                <h3 className="font-display font-semibold text-lg text-white mb-2">
                  {title}
                </h3>
                <p className="text-white/65 text-sm leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section
        className="relative py-20 bg-[#050A1F] overflow-hidden"
        data-testid="acil-process"
      >
        <div className="absolute inset-0 bg-blueprint opacity-25" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-gold uppercase tracking-[0.4em] text-xs mb-3">
              Nasıl Çalışıyor?
            </p>
            <h2 className="font-display text-3xl sm:text-5xl font-bold leading-[1.05] mb-6 text-balance">
              <span className="text-gradient-silver">Aramadan teslime </span>
              <span className="text-gradient-gold">4 adımda akü değişimi.</span>
            </h2>

            <ol className="space-y-5 mt-8">
              {[
                {
                  t: "Bizi arayın veya WhatsApp'tan konum gönderin",
                  d: "0542 665 26 26 numarasını arayın ya da konumunuzu doğrudan WhatsApp üzerinden iletin.",
                },
                {
                  t: "Aracınızı ve marka tercihinizi söyleyin",
                  d: "İnci Akü, Varta veya Bosch — modeline uygun aküyü beraber belirleyelim.",
                },
                {
                  t: "Uzman ekibimiz adresinize gelsin",
                  d: "Yerinde montaj hizmetimiz ücretlidir (servis ücreti vardır). Hızlıca yola çıkarız.",
                },
                {
                  t: "Tüm kontroller + montaj + garanti",
                  d: "Kaçak ölçümü, elektrik kontrolü, eski akü ölçümü, yeni tarihli akü montajı ve ölçüm sonrası teslim.",
                },
              ].map((s, i) => (
                <li
                  key={s.t}
                  className="flex items-start gap-4"
                  data-testid={`process-step-${i}`}
                >
                  <div className="w-10 h-10 rounded-xl gradient-gold flex items-center justify-center text-black font-display font-bold flex-shrink-0">
                    {i + 1}
                  </div>
                  <div>
                    <div className="font-display font-semibold text-white mb-1">
                      {s.t}
                    </div>
                    <p className="text-white/65 text-sm leading-relaxed">
                      {s.d}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <div className="relative">
            <div className="absolute -inset-4 bg-gold/10 blur-3xl rounded-full" />
            <div className="relative rounded-3xl overflow-hidden glass-gold">
              <img
                src={IMAGES.battery}
                alt="Eskişehir yerinde akü değişimi"
                className="w-full h-[500px] object-cover"
                loading="lazy"
              />
              <div className="absolute top-5 left-5 right-5 glass-strong rounded-xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-gold font-semibold">
                    Yerinde Hizmet
                  </div>
                  <div className="font-display font-bold text-white">
                    Eskişehir Geneli
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ServiceChecklistSection />
      <BrandShowcaseSection />

      {/* Coverage Areas */}
      <section
        className="relative py-20 overflow-hidden"
        data-testid="coverage-areas"
      >
        <div className="absolute inset-0 spotlight-gold opacity-25" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10 max-w-2xl">
            <p className="text-gold uppercase tracking-[0.4em] text-xs mb-3">
              Hizmet Bölgeleri
            </p>
            <h2 className="font-display text-3xl sm:text-5xl font-bold text-balance leading-[1.05]">
              <span className="text-gradient-silver">Eskişehir'in </span>
              <span className="text-gradient-gold">her noktasına geliyoruz.</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {[
              { label: "Tepebaşı", to: "/tepebasi-aku-degisimi" },
              { label: "Odunpazarı", to: "/odunpazari-aku-degisimi" },
              { label: "Emek Mahallesi", to: "/emek-mahallesi-aku-degisimi" },
              { label: "Batıkent", to: "/batikent-aku-degisimi" },
              { label: "Sazova", to: "/sazova-aku-degisimi" },
            ].map((a) => (
              <Link
                key={a.to}
                to={a.to}
                data-testid={`coverage-link-${a.to.slice(1)}`}
                className="group glass rounded-xl px-5 py-4 flex items-center justify-between hover:border-gold/50 transition-all"
              >
                <span className="font-display font-semibold text-white text-sm">
                  {a.label}
                </span>
                <ArrowRight className="w-4 h-4 text-gold opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-inci-blue relative overflow-hidden">
        <div className="absolute inset-0 bg-blueprint opacity-20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-white" />
          <h2 className="font-display text-3xl sm:text-5xl font-bold text-white mb-4 leading-[1.1] text-balance">
            Aracınız çalışmıyor mu? Hemen ulaşın.
          </h2>
          <p className="text-white/85 max-w-2xl mx-auto mb-8">
            Star Otogaz uzman ekibi olarak Eskişehir genelinde yerinde akü
            değişimi yapıyoruz. İnci Akü, Varta ve Bosch — orijinal seri, gerçek
            garanti.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={`tel:${BRAND.phoneTel}`}
              className="bg-gold hover:bg-gold-light text-black font-semibold px-7 py-3.5 inline-flex items-center gap-2 justify-center transition-all"
            >
              <Phone className="w-4 h-4" /> {BRAND.phone}
            </a>
            <a
              href={buildWhatsAppLink(waText)}
              target="_blank"
              rel="noreferrer"
              className="bg-[#25D366] hover:bg-[#1ebd5a] text-white font-semibold px-7 py-3.5 inline-flex items-center gap-2 justify-center transition-all"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp Konum Gönder
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
